import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

const WS_BASE = process.env.REACT_APP_WS_URL || `wss://${window.location.host}`;
const API = process.env.REACT_APP_API_URL || '';

// Reconexión con espera creciente: 2s, 4s, 8s... hasta 30s.
// Evita martillar Apache/nginx cuando el backend está caído o reiniciando.
const BASE_DELAY = 2000;
const MAX_DELAY = 30000;

export function useWebSocket(onMessage) {
  const { getToken, logout } = useAuth();
  const wsRef = useRef(null);
  const [status, setStatus] = useState('connecting');
  const reconnectRef = useRef(null);
  const attemptsRef = useRef(0);
  const stoppedRef = useRef(false);   // true al desmontar: no reconectar
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  // El navegador no expone el código HTTP cuando el servidor rechaza el WebSocket
  // (401 en el upgrade). Se verifica la sesión por REST antes de reintentar.
  const sessionIsValid = useCallback(async () => {
    const token = getToken();
    if (!token) return false;
    try {
      const res = await fetch(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      return res.status !== 401;
    } catch {
      return true; // error de red: la sesión puede estar bien, se reintenta
    }
  }, [getToken]);

  const connect = useCallback(() => {
    if (stoppedRef.current) return;
    const token = getToken();
    if (!token) return;

    const ws = new WebSocket(`${WS_BASE}/ws?token=${encodeURIComponent(token)}`);
    wsRef.current = ws;

    ws.onopen = () => {
      attemptsRef.current = 0;
      setStatus('connected');
    };

    ws.onmessage = (event) => {
      try {
        onMessageRef.current?.(JSON.parse(event.data));
      } catch {}
    };

    ws.onclose = async () => {
      if (stoppedRef.current) return;
      setStatus('disconnected');

      if (!(await sessionIsValid())) {
        logout();            // token rechazado: volver al login en vez de reintentar sin fin
        return;
      }

      const delay = Math.min(BASE_DELAY * 2 ** attemptsRef.current, MAX_DELAY);
      attemptsRef.current += 1;
      reconnectRef.current = setTimeout(connect, delay);
    };

    ws.onerror = () => {
      setStatus('error');
      ws.close();
    };
  }, [getToken, logout, sessionIsValid]);

  useEffect(() => {
    stoppedRef.current = false;
    connect();
    return () => {
      stoppedRef.current = true;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      wsRef.current?.close();
    };
  }, [connect]);

  return status;
}
